import { useState, useEffect, useCallback } from "react";
import { API } from "@/App";
import axios from "axios";
import { logError } from "@/lib/logger";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { X } from "lucide-react";
import { EditableText as T } from "@/components/live/EditableText";

const Gallery = () => {
  const [images, setImages] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [activeCategory, setActiveCategory] = useState("portraits");
  const [loading, setLoading] = useState(true);

  // Helper to get full image URL
  const getImageUrl = (url) => {
    if (!url) return '';
    // If it's a relative API path, prepend the backend URL
    if (url.startsWith('/api/')) {
      return `${process.env.REACT_APP_BACKEND_URL}${url}`;
    }
    return url;
  };

  const fetchImages = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/public/gallery`);
      if (response.data && response.data.length > 0) {
        setImages(response.data);
      } else {
        setImages([]);
      }
    } catch (error) {
      logError("Failed to fetch gallery images:", error);
      setImages([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchImages();
  }, [fetchImages]);

  const categories = [
    { key: "portraits", label: "Portraits" },
    { key: "lifestyle", label: "Lifestyle" },
    { key: "featured", label: "Featured In" },
  ];

  const filteredImages = images.filter(img => img.category === activeCategory);

  return (
    <div data-testid="gallery-page">
      {/* Header */}
      <section className="page-header" data-testid="gallery-header">
        <div className="max-w-4xl mx-auto px-6">
          <T k="gallery.visual-portfolio" as="p" className="font-body text-sm tracking-widest uppercase text-champagne-gold mb-4 animate-fade-in">Visual Portfolio</T>
          <T k="gallery.gallery" as="h1" className="page-title animate-fade-in stagger-1">Gallery</T>
          <T k="gallery.a-curated-collection" as="p" className="page-subtitle animate-fade-in stagger-2">A curated collection capturing the essence of my life in front of the lens and the art of sensuality.</T>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 px-6 md:px-12 lg:px-24" data-testid="gallery-filter">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap justify-center gap-4">
            {categories.map((cat) => (
              <button
                key={cat.key}
                onClick={() => setActiveCategory(cat.key)}
                className={`px-6 py-2 rounded-full font-body text-sm tracking-widest uppercase transition-all duration-300 ${
                  activeCategory === cat.key
                    ? "bg-jade-deep text-white"
                    : "bg-white text-stone-grey hover:bg-beige"
                }`}
                data-testid={`filter-${cat.key}`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-12 px-6 md:px-12 lg:px-24" data-testid="gallery-grid">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="spinner" />
            </div>
          ) : (
            <div className="masonry-gallery">
              {filteredImages.map((image, index) => (
                <div
                  key={image.id}
                  className="gallery-item cursor-pointer mb-4"
                  onClick={() => setSelectedImage(image)}
                  data-testid={`gallery-image-${index}`}
                >
                  <img
                    src={getImageUrl(image.url)}
                    alt={image.alt}
                    className="w-full rounded-lg"
                    loading="lazy"
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Lightbox Dialog */}
      <Dialog open={!!selectedImage} onOpenChange={() => setSelectedImage(null)}>
        <DialogContent className="max-w-4xl bg-charcoal border-none p-0">
          <button
            onClick={() => setSelectedImage(null)}
            className="absolute top-4 right-4 z-10 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors"
            data-testid="close-lightbox"
          >
            <X className="w-5 h-5 text-white" />
          </button>
          {selectedImage && (
            <img
              src={getImageUrl(selectedImage.url)}
              alt={selectedImage.alt}
              className="w-full h-auto max-h-[80vh] object-contain"
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Notice */}
      <section className="py-16 px-6 md:px-12 lg:px-24 bg-blush-pink/20">
        <div className="max-w-2xl mx-auto text-center">
          <p className="font-body text-sm text-stone-grey leading-relaxed">
            <T k="gallery.note" className="font-medium">Note:</T> Additional private images are available upon verification and mutual interest. 
            These photos represent my authentic self — what you see is what you get.
          </p>
        </div>
      </section>
    </div>
  );
};

export default Gallery;
